// AM Advertising Home — alert acknowledgement ("I've seen it"). PURE + tested,
// shared by every page and the server service.
//
// ⚠️⚠️ ACKNOWLEDGEMENT IS A FILTER APPLIED OVER EVALUATED ALERTS, NEVER A
// CHANGE TO THE RULES. `evaluateAlerts` knows nothing about acks and must stay
// that way — the Slack digest calls THAT unchanged, and a digest that silently
// inherited someone's dashboard rules would be a different feature. The
// suppression happens HERE, after evaluation, and everything it hides is
// counted and revealable. (⚠️ Do NOT read that as "the digest skips acks": it
// applies this filter too, on the `'am'` surface — see AckSurface below.)
//
// TWO WAYS AN ACK STOPS SUPPRESSING:
//  1. EXPIRY (72h, evaluated at READ time — no cleanup cron). If the condition
//     still holds after that, it re-alerts.
//  2. ⚠️ ESCALATION. If the alert's severity RISES while acknowledged (amber →
//     red — a 1.6× CPL spike becoming 2.4×), it re-appears IMMEDIATELY. An ack
//     means "I've seen this situation", not "mute this account", and a
//     materially worse situation is a new situation. Mutation-checked.

import type {
  AckSurface,
  AdAlertSeverity,
  AdAlertType,
  AdChannel,
  AlertAcknowledgement,
} from '../../types/advertisingHome.js';
import type { HomeAlert } from './alerts.js';

/** How long an acknowledgement suppresses its alert. */
export const ACK_WINDOW_HOURS = 72;
const ACK_WINDOW_MS = ACK_WINDOW_HOURS * 60 * 60 * 1000;

/**
 * WHICH BOARD an ack was cleared from — the AM's Advertising Home, or one of
 * the single-channel specialist pages. Acks do NOT cross between them: the Meta
 * team clearing something leaves it outstanding for the AM, and vice versa.
 *
 * ⚠️⚠️ TWO-VALUED, NOT `'am' | 'meta' | 'google'`. The CHANNEL is already a
 * component of the key, so a Meta alert's specialist surface can only ever be
 * the Meta page — a three-valued union would let a caller construct
 * `acct__meta__no_spend__google`, a combination that means nothing. Two values
 * make that unrepresentable rather than merely discouraged.
 *
 * ⚠️ THE SURFACE IS THE PAGE, NOT THE PERSON. There is no specialist role
 * (`UserRole` is `'admin' | 'staff'`, and `User.team` is populated by nothing),
 * and inventing one is a new concept with real cost that this feature does not
 * need. So an AM working the Meta board and clearing something there clears the
 * SPECIALIST side — a deliberate consequence, and the right one: the model then
 * has no dependency on identity at all, which removes a whole class of bug.
 *
 * Declared in src/types/advertisingHome.ts (an ack PERSISTS it, so it is
 * storage vocabulary) and re-exported here, the AlertType/AlertSeverity
 * pattern — so there is exactly one definition of the two-valued union.
 */
export type { AckSurface };

/**
 * The alert's STABLE identity — account + channel + type. Deliberately NOT the
 * message or the figures: those move with every pull while the situation is
 * unchanged, and a key built on them would evaporate the moment a number
 * ticked.
 *
 * ⚠️ SHARED BY ACKS AND NOTES, and that sharing is the point: a note written
 * against an alert must still be findable when the ack on it has expired, so
 * both sides must agree about what "the same alert" means. Acks then append
 * their surface (they are per-board); notes do not (they are shared).
 */
export function alertIdentityKey(
  accountId: string,
  channel: AdChannel,
  alertType: AdAlertType,
): string {
  return `${accountId}__${channel}__${alertType}`;
}

/**
 * The ack's key — the alert identity plus the SURFACE it was cleared from.
 * Doubles as the Firestore doc id (every part is an id or an enum literal, so
 * it is document-path safe).
 *
 * ⚠️ A pre-2026-09-17 doc id is the three-part identity with no surface, so it
 * matches no computed key and suppresses nothing. That is the intended
 * (script-free) migration — those docs expire inside 72h. See §3g of the brief.
 */
export function ackKey(
  accountId: string,
  channel: AdChannel,
  alertType: AdAlertType,
  surface: AckSurface,
): string {
  return `${alertIdentityKey(accountId, channel, alertType)}__${surface}`;
}

export function alertAckKey(alert: HomeAlert, surface: AckSurface): string {
  return ackKey(alert.accountId, alert.channel, alert.type, surface);
}

/** The three-part identity of an alert — what a NOTE is filed against. */
export function alertNoteKey(alert: HomeAlert): string {
  return alertIdentityKey(alert.accountId, alert.channel, alert.type);
}

/** Red is worse than amber; the ONE ordering the escalation rule reads. */
const SEVERITY_RANK: Record<AdAlertSeverity, number> = { amber: 1, red: 2 };

/** Has the situation got materially worse than when it was acknowledged? */
export function isEscalation(current: AdAlertSeverity, atAck: AdAlertSeverity): boolean {
  return SEVERITY_RANK[current] > SEVERITY_RANK[atAck];
}

/** Is the ack still within its window? Unparseable/absent expiry → expired. */
export function isAckActive(ack: AlertAcknowledgement, nowMs: number): boolean {
  const expires = Date.parse(ack.expiresAt);
  return Number.isFinite(expires) && expires > nowMs;
}

/** The expiry stamped at ack time. */
export function ackExpiresAt(nowMs: number): string {
  return new Date(nowMs + ACK_WINDOW_MS).toISOString();
}

/** An alert currently suppressed, paired with the ack doing the suppressing. */
export interface AcknowledgedAlert {
  alert: HomeAlert;
  acknowledgement: AlertAcknowledgement;
}

export interface AckSplit {
  /** What still needs work — the panel's list. */
  visible: HomeAlert[];
  /** Suppressed: counted, revealable, never silently dropped. */
  acknowledged: AcknowledgedAlert[];
}

/**
 * Split evaluated alerts into outstanding vs acknowledged, ON ONE SURFACE.
 *
 * An alert is suppressed only when its ack for THIS surface is BOTH unexpired
 * AND not escalated past the severity it was acknowledged at. Anything else
 * stays in `visible` — including an alert whose ack has expired, which is
 * exactly the "re-alerts after 72h" behaviour, and including an alert cleared
 * on the OTHER surface.
 *
 * ⚠️⚠️ `surface` IS REQUIRED AND HAS NO DEFAULT, because a default would be
 * silently wrong for two of the three callers. In particular the Slack digest
 * is the AM's own opt-in morning DM, so it passes `'am'`: a digest reading the
 * specialist surface would suppress an AM's DM on the strength of the Meta
 * team's clears, and nobody would ever see it happen. A required argument
 * prevents OMISSION, not a wrong VALUE — hence a named test per caller.
 *
 * ⚠️⚠️ AND THE ABSENCE OF A DEFAULT IS ONLY ENFORCED BY THE COMPILER — MEASURED,
 * NOT ASSUMED. With `surface` required, deleting the argument at the digest's
 * call site is `TS2554: Expected 4 arguments, but got 3`. Add a
 * `= 'am'` default and that same deletion typechecks with ZERO errors and
 * silently hands every omitting caller the AM board's clears. There is NO
 * runtime test that can catch it, because a default is unobservable at a call
 * site that passes explicitly — so if you are about to add a "harmless"
 * default here, that is the thing you would be removing.
 */
export function applyAcknowledgements(
  alerts: readonly HomeAlert[],
  acks: readonly AlertAcknowledgement[],
  nowMs: number,
  surface: AckSurface,
): AckSplit {
  const byKey = new Map(acks.map((a) => [a.id, a]));
  const visible: HomeAlert[] = [];
  const acknowledged: AcknowledgedAlert[] = [];

  for (const alert of alerts) {
    const ack = byKey.get(alertAckKey(alert, surface));
    if (
      ack &&
      isAckActive(ack, nowMs) &&
      !isEscalation(alert.severity, ack.severityAtAck)
    ) {
      acknowledged.push({ alert, acknowledgement: ack });
    } else {
      visible.push(alert);
    }
  }
  return { visible, acknowledged };
}

/** "Ethan, 2h ago" — the attribution line on a revealed acknowledgement. */
export function formatAckedBy(ack: AlertAcknowledgement, nowMs: number): string {
  const at = Date.parse(ack.acknowledgedAt);
  if (!Number.isFinite(at)) return ack.acknowledgedByName;
  const mins = Math.max(0, Math.round((nowMs - at) / 60_000));
  const ago =
    mins < 1 ? 'just now' : mins < 60 ? `${mins}m ago` : mins < 60 * 24
      ? `${Math.round(mins / 60)}h ago`
      : `${Math.round(mins / (60 * 24))}d ago`;
  return `${ack.acknowledgedByName}, ${ago}`;
}
